import {
  MRT_GlobalFilterTextField,
  MRT_TableBodyCellValue,
  MRT_TablePagination,
  MRT_ToolbarAlertBanner,
  flexRender,
  useMaterialReactTable,
} from "material-react-table";
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Select,
  MenuItem,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useMemo } from "react";
import { FIELD, fieldLabelSx, fieldRootSx } from "../selections/fieldTheme";
import TableSkeletonLoader from "./tableSkeletionLoader";

// Search box styled like FilterMultiSelect (shared fieldTheme: label above, rounded green field).
// Bound to the table's global filter, so it searches exactly like the default search field.
const TABLE_ACCENT = "#198754"; // brand green (modern style)
const SELECT_ACCENT = "#2563eb"; // blue for checkboxes / selected rows
const SELECT_OUTLINE = "#93c5fd"; // light blue (top of the selected-row marker gradient)

const StyledSearch = ({ table }) => {
  const value = table.getState().globalFilter ?? "";
  return (
    <Box>
      <Typography component="label" htmlFor="table-search" sx={fieldLabelSx}>
        Search
      </Typography>
      <TextField
        id="table-search"
        fullWidth
        size="small"
        placeholder="Search all columns..."
        value={value}
        onChange={(e) => table.setGlobalFilter(e.target.value || undefined)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: FIELD.accent, fontSize: 20 }} />
            </InputAdornment>
          ),
          endAdornment: value ? (
            <InputAdornment position="end">
              <IconButton size="small" aria-label="clear search" onClick={() => table.setGlobalFilter(undefined)} sx={{ color: FIELD.placeholder, "&:hover": { color: FIELD.accent, bgcolor: FIELD.selectedBg } }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{ "& .MuiOutlinedInput-root": fieldRootSx }}
      />
    </Box>
  );
};

const DataTableWithCheckbox = ({
  columns,
  data,
  renderRowActions,
  onSelectionChange,
  enableRowSelection,
  renderFilters,
  status,
  searchFirst = false, // opt-in: styled search box on the left, filters after it
  modern = searchFirst, // opt-in: light modern table style (on by default with searchFirst)
  emptyMessage, // optional text shown in the table body when there are no rows
  selectionResetKey, // optional: when this value changes, row selection is cleared (e.g. a filter changed)
}) => {
  const dataWithSno = useMemo(
    () =>
      data.map((item, index) => ({
        ...item,
        sno: index + 1,
      })),
    [data]
  );

  const table = useMaterialReactTable({
    columns,
    data: dataWithSno,
    enableRowSelection,
    enableRowActions: Boolean(renderRowActions),
    renderRowActions,
    positionActionsColumn: "last",
    initialState: {
      pagination: { pageSize: 10, pageIndex: 0 },
      showGlobalFilter: true,
    },
    muiPaginationProps: {
      rowsPerPageOptions: [5, 10, 15, { label: "All", value: data.length }],
      variant: "outlined",
      showRowsPerPage: !modern,
    },
    paginationDisplayMode: "pages",
  });

  // Toolbar: grey panel only when the page passes filters; search alone sits plainly above the table
  const hasFilters = Boolean(renderFilters);
  const toolbarSx = hasFilters
    ? {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "flex-end",
      gap: { xs: 1.5, sm: 2 },
      mb: 2,
      p: { xs: 1.5, sm: 2 },
      borderRadius: 3,
      bgcolor: "#f8fafc",
      border: "1px solid #e9edf2",
    }
    : {
      // search alone: a light strip at the top of the same box as the table
      display: "flex",
      px: { xs: 1.5, sm: 2 },
      pt: 1.25,
      pb: 1.5,
      bgcolor: "#fafbfc",
      borderBottom: "1px solid #e5e7eb",
    };
  // Search + table + footer in one bordered box (only when search is the sole control)
  const oneBox = modern && searchFirst && !hasFilters;
  const oneBoxSx = oneBox ? { border: "1px solid #e5e7eb", borderRadius: 3, bgcolor: "#fff", overflow: "hidden" } : {};

  // Clear selection when the page's filters change (row ids are positions, so old ticks would be wrong)
  useEffect(() => {
    if (selectionResetKey !== undefined) table.resetRowSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionResetKey]);

  // Track selection changes
  useEffect(() => {
    const selectedData = table.getSelectedRowModel().rows.map(
      (row) => row.original
    );
    onSelectionChange?.(selectedData);
  }, [table.getState().rowSelection, onSelectionChange]);


  if (["loading", "pending", "fetching"].includes(status)) {
    // Modern style: same toolbar layout + light skeleton (no dark header, no layout jump)
    if (modern) {
      return (
        <Stack sx={{ m: searchFirst ? "0.5rem 0 1rem" : "2rem 0", minWidth: 0, maxWidth: "100%", ...oneBoxSx }}>
          {searchFirst && (
            <Box sx={toolbarSx}>
              <Box sx={{ width: { xs: "100%", sm: 280 }, flexShrink: 0 }}>
                <StyledSearch table={table} />
              </Box>
              {hasFilters && <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 260 } }}>{renderFilters}</Box>}
            </Box>
          )}
          <Box sx={oneBox ? { "& > *": { border: "none !important", borderRadius: "0 !important" } } : undefined}>
            <TableSkeletonLoader columnsCount={columns.length || 6} rowsCount={8} modern />
          </Box>
        </Stack>
      );
    }
    return (
      <Box sx={{ p: 2 }}>
        {renderFilters && <Box mb={2}>{renderFilters}</Box>}
        <TableSkeletonLoader columnsCount={columns.length || 6} rowsCount={10} />
      </Box>
    );
  }


  return (
    // minWidth 0 / maxWidth 100%: a wide table scrolls inside its container instead of stretching the page
    <Stack sx={{ m: searchFirst ? "0.5rem 0 1rem" : "2rem 0", minWidth: 0, maxWidth: "100%", ...oneBoxSx }}>
      {searchFirst ? (
        // Toolbar panel: search first, then the page's filters/actions (renderFilters)
        <Box sx={toolbarSx}>
          <Box sx={{ width: { xs: "100%", sm: 280 }, flexShrink: 0 }}>
            <StyledSearch table={table} />
          </Box>
          {hasFilters && <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 260 } }}>{renderFilters}</Box>}
        </Box>
      ) : (
        <Grid container spacing={1} mb={2}>
          <Grid item size={{ xs: 12, md: 8 }}>{renderFilters}</Grid>
          <Grid item size={{ xs: 12, md: 4 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}><MRT_GlobalFilterTextField table={table} /></Grid>
        </Grid>
      )}

      <TableContainer
        sx={{
          overflow: "auto",
          maxWidth: "100%",
          "&::-webkit-scrollbar": { height: "6px", width: "6px" },
          "&::-webkit-scrollbar-thumb": { backgroundColor: modern ? "#cbd5e1" : "#888", borderRadius: "4px" },
          "&::-webkit-scrollbar-thumb:hover": { backgroundColor: modern ? "#94a3b8" : "#555" },
          scrollbarWidth: "thin",
          scrollbarColor: modern ? "#cbd5e1 transparent" : "#888 #f1f1f1",
          ...(modern && {
            borderRadius: oneBox ? 0 : 3,
            border: oneBox ? "none" : "1px solid #e5e7eb",
            bgcolor: "#fff",
            // Brand-green checkboxes
            "& .MuiCheckbox-root": { color: "#cbd5e1", p: 0.5 }, // compact checkbox (keeps rows short)
            // Slightly smaller text inside cells (also applies to Typography/Chip used in custom cells)
            "& .MuiTableBody-root .MuiTypography-body2": { fontSize: 13 },
            "& .MuiTableBody-root .MuiChip-label": { fontSize: 12 },
            "& .MuiCheckbox-root.Mui-checked, & .MuiCheckbox-root.MuiCheckbox-indeterminate": { color: SELECT_ACCENT },
          }),
        }}
      >
        <Table stickyHeader={modern}>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    align="center"
                    sx={
                      modern
                        ? {
                            position: header.id === "actions" ? "sticky" : undefined,
                            right: header.id === "actions" ? 0 : "auto",
                            zIndex: header.id === "actions" ? 3 : 2,
                            width: header.id === "actions" ? 120 : "auto",
                            whiteSpace: "nowrap",
                            px: 1.3,
                            py: 1,
                            fontSize: 11.5,
                            fontWeight: 800,
                            letterSpacing: 0.6,
                            textTransform: "uppercase",
                            color: "#4b5563",
                            bgcolor: "#f3f4f6", // light ash header
                            borderBottom: "1px solid #d1d5db",
                          }
                        : {
                            fontWeight: "bold",
                            textTransform: "capitalize",
                            backgroundColor: "var(--primary)",
                            color: "#fff",
                            position: header.id === "actions" ? "sticky" : "static",
                            right: header.id === "actions" ? 0 : "auto",
                            zIndex: header.id === "actions" ? 2 : 1,
                            width: header.id === "actions" ? 120 : "auto",
                            whiteSpace: "nowrap",
                            padding: "2px",
                            borderRight: "2px solid rgba(226, 224, 224, 0.8)",
                          }
                    }
                  >
                    {header.id === "mrt-row-select" ? (
                      <Checkbox
                        checked={table.getIsAllRowsSelected()}
                        indeterminate={table.getIsSomeRowsSelected()}
                        onChange={table.getToggleAllRowsSelectedHandler()}
                      />
                    ) : header.isPlaceholder ? null : (
                      flexRender(
                        header.column.columnDef.header ?? header.column.columnDef.Header,
                        header.getContext()
                      )
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>

          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  align="center"
                  sx={{ padding: modern ? "40px 20px" : "20px", color: modern ? "#94a3b8" : undefined }}
                >
                  {(() => {
                    const search = table.getState().globalFilter;
                    const text = search
                      ? `No results for "${search}"`
                      : emptyMessage || "No Data Found";
                    return modern ? (
                      <Stack alignItems="center" spacing={0.75}>
                        <SearchOffIcon sx={{ fontSize: 30, color: "#cbd5e1" }} />
                        <Typography variant="body2" fontWeight={600} color="#64748b">
                          {text}
                        </Typography>
                      </Stack>
                    ) : (
                      text
                    );
                  })()}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row, rowIndex) => (
                <TableRow
                  key={row.id}
                  selected={row.getIsSelected()}
                  sx={
                    modern
                      ? {
                          transition: "background-color .15s ease",
                          "&:nth-of-type(even)": { bgcolor: "#fafafa" },
                          "&:hover": { bgcolor: "#f4f4f5" },
                          // Selected: row itself stays normal; only the checkbox cell (left side) is highlighted
                          "&.Mui-selected": { bgcolor: "transparent" },
                          "&.Mui-selected:nth-of-type(even)": { bgcolor: "#fafafa" },
                          "&.Mui-selected:hover": { bgcolor: "#f4f4f5" },
                          // Glowing pill marker on the left edge of selected rows (like the sidebar's active item)
                          "& > td:first-of-type": { position: "relative" },
                          "&.Mui-selected > td:first-of-type::before": {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            top: "18%",
                            bottom: "18%",
                            width: 4,
                            borderRadius: "0 4px 4px 0",
                            background: `linear-gradient(180deg, ${SELECT_OUTLINE}, ${SELECT_ACCENT})`,
                            boxShadow: `0 0 10px ${SELECT_ACCENT}99`,
                          },
                          "&:last-child > td": { borderBottom: "none" },
                        }
                      : undefined
                  }
                >
                  {row.getVisibleCells().map((cell) => {
                    const cellValue = cell.getValue();
                    return (
                      <TableCell
                        key={cell.id}
                        align="center"
                        sx={
                          modern
                            ? {
                                position: cell.column.id === "actions" ? "sticky" : "static",
                                right: cell.column.id === "actions" ? 0 : "auto",
                                backgroundColor: cell.column.id === "actions" ? "#fff" : "inherit",
                                zIndex: cell.column.id === "actions" ? 1 : 0,
                                width: cell.column.id === "actions" ? 120 : "auto",
                                whiteSpace: "nowrap",
                                px: 1.3,
                                py: 0.6,
                                fontSize: 13,
                                color: "#334155",
                                borderBottom: "1px solid #ececee",
                              }
                            : {
                                border: "1px solid rgba(226,224,224,0.5)",
                                borderRight: "1px solid rgba(0,0,0,0.12)", // Optional for body
                                position: cell.column.id === "actions" ? "sticky" : "static",
                                right: cell.column.id === "actions" ? 0 : "auto",
                                backgroundColor: cell.column.id === "actions" ? "#fff" : "inherit",
                                zIndex: cell.column.id === "actions" ? 1 : 0,
                                width: cell.column.id === "actions" ? 120 : "auto",
                                whiteSpace: "nowrap",
                                padding: "5px",
                              }
                        }
                      >
                        {cellValue === null || cellValue === "" ? (
                          "-"
                        ) : (
                          <MRT_TableBodyCellValue
                            cell={cell}
                            table={table}
                            staticRowIndex={rowIndex}
                          />
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {modern ? (
        // Footer: "Showing x–y of z" on the left, styled pagination on the right
        (() => {
          const { pageIndex, pageSize } = table.getState().pagination;
          const total = table.getFilteredRowModel().rows.length;
          const from = total ? pageIndex * pageSize + 1 : 0;
          const to = Math.min(total, (pageIndex + 1) * pageSize);
          const selectedCount = table.getSelectedRowModel().rows.length;
          return (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: { xs: "center", sm: "space-between" },
                gap: 1,
                ...(oneBox ? { px: { xs: 1.5, sm: 2 }, py: 1.25, borderTop: "1px solid #e5e7eb", bgcolor: "#fafbfc" } : { mt: 1.5 }),
                // Phones: smaller page buttons so the pagination fits on one line
                "@media (max-width: 600px)": {
                  "& .MuiPaginationItem-root": { minWidth: 28, height: 28, fontSize: 12.5, mx: 0.25 },
                },
                "& .MuiPaginationItem-root": { borderRadius: 2, fontWeight: 600, borderColor: "#e2e8f0" },
                "& .MuiPaginationItem-root.Mui-selected": {
                  color: "#fff",
                  bgcolor: TABLE_ACCENT,
                  borderColor: TABLE_ACCENT,
                  "&:hover": { bgcolor: "#157347" },
                },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
                {/* Rows per page (left) */}
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                    Rows per page
                  </Typography>
                  <Select
                    size="small"
                    value={pageSize}
                    onChange={(e) => table.setPageSize(Number(e.target.value))}
                    IconComponent={KeyboardArrowDownIcon}
                    MenuProps={{ PaperProps: { sx: { borderRadius: 2, mt: 0.5 } } }}
                    sx={{
                      height: 34,
                      minWidth: 74,
                      borderRadius: 2,
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#1e293b",
                      bgcolor: "#fff",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: TABLE_ACCENT },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: TABLE_ACCENT, borderWidth: "1.5px" },
                      "& .MuiSelect-icon": { color: TABLE_ACCENT },
                    }}
                  >
                    {[5, 10, 15].map((n) => (
                      <MenuItem key={n} value={n}>{n}</MenuItem>
                    ))}
                    {![5, 10, 15].includes(data.length) && data.length > 0 && (
                      <MenuItem value={data.length}>All</MenuItem>
                    )}
                  </Select>
                </Stack>

                <Box sx={{ width: "1px", height: 20, bgcolor: "#e2e8f0", display: { xs: "none", sm: "block" } }} />

                <Typography variant="body2" color="text.secondary">
                  Showing <b>{from}</b>–<b>{to}</b> of <b>{total}</b>
                  {selectedCount > 0 && (
                    <>
                      <Box component="span" sx={{ ml: 1, color: SELECT_ACCENT, fontWeight: 700 }}>
                        · {selectedCount} selected
                      </Box>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => table.resetRowSelection()}
                        sx={{
                          ml: 1,
                          px: 1,
                          py: 0.25,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.4,
                          verticalAlign: "middle",
                          border: "1px solid #e2e8f0",
                          borderRadius: 5,
                          bgcolor: "#fff",
                          color: "#64748b",
                          fontSize: 12,
                          fontWeight: 600,
                          fontFamily: "inherit",
                          cursor: "pointer",
                          transition: "all .15s ease",
                          "&:hover": { color: "#dc2626", borderColor: "#fecaca", bgcolor: "#fef2f2" },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 13 }} />
                        Clear
                      </Box>
                    </>
                  )}
                </Typography>
              </Stack>

              {/* Page buttons (right) */}
              <MRT_TablePagination table={table} />
            </Box>
          );
        })()
      ) : (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
          <MRT_TablePagination table={table} />
        </Box>
      )}

      {!modern && <MRT_ToolbarAlertBanner stackAlertBanner table={table} />}
    </Stack>
  );
};

export default DataTableWithCheckbox;
